import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Check, Sparkles, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  PLANS,
  displayPrice,
  annualMonthlyEquivalent,
  FOUNDING_MEMBER_PRICE_PENCE,
  FOUNDING_MEMBER_PLAN,
} from '../../config/pricing';

/**
 * Plan-selection modal used from the Manager Billing page.
 *
 * Behaviour:
 *  - Lists every commercial plan (PAYG / Monthly / Annual / Multi-site)
 *    with prices pulled from the frontend source-of-truth `PLANS`
 *    (kept in lockstep with backend `pricing.py`).
 *  - Founding Member is surfaced as an INLINE offer on the Monthly card
 *    (not as its own plan) so we don't mislead users into thinking it's
 *    a permanent tier — it's just a £10-forever coupon on Monthly for
 *    the first 20 subscribers.
 *  - Every selection calls `POST /api/payments/create-checkout` and
 *    redirects to the returned Stripe Checkout URL. Backend already
 *    handles PAYG-vs-subscription price mapping (metered vs one-off).
 *  - Errors show inline in the modal (not silent toast) so the manager
 *    can see WHY checkout failed (bad plan / Stripe down / missing key).
 */
export default function PlanSelectionDialog({
  open,
  onOpenChange,
  currentPlanType,
  isFoundingMember = false,
  hasActiveSub = false,
  onChanged,
}) {
  const { api } = useAuth();
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/founding-members/status');
        setFoundingStatus(res.data);
      } catch {
        setFoundingStatus({ enabled: false, seatsRemaining: 0 });
      }
    })();
  }, [open, api]);

  // A user sees the Founding-Member price ONLY when either
  //   (a) they already own the slot (isFoundingMember=true from backend), or
  //   (b) the offer is still open (seatsRemaining>0) AND they have never
  //       taken a Monthly plan yet.
  // This stops the misleading "£19.99" price appearing to users who
  // aren't actually eligible.
  const foundingEligible = Boolean(
    isFoundingMember ||
    (foundingStatus?.enabled &&
      (foundingStatus?.seatsRemaining ?? 0) > 0 &&
      currentPlanType !== 'monthly'),
  );

  const handleChoose = async (planType) => {
    setError(null);
    setLoadingPlan(planType);
    try {
      // iter90: if the user has an active subscription, this is a plan
      // change (upgrade / downgrade / switch). Show the confirm dialog
      // with proration + effective date, then execute via change-plan.
      // Otherwise fall back to the standard create-checkout flow (new
      // subscribers or PAYG users starting their first recurring plan).
      if (hasActiveSub && currentPlanType && currentPlanType !== 'none') {
        const preview = await api.post('/payments/preview-plan-change', { newPlanType: planType });
        setPreviewData({ ...preview.data, targetPlan: planType });
        return;
      }
      const res = await api.post('/payments/create-checkout', {
        planType,
        originUrl: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError('The payment provider returned no checkout URL. Please try again in a moment.');
    } catch (err) {
      const detailObj = err?.response?.data?.detail;
      const isDuplicate =
        err?.response?.status === 409 && detailObj?.code === 'subscription_exists';
      const detailMsg = isDuplicate
        ? detailObj.message
        : detailObj?.message || detailObj || err?.message ||
          'Could not start checkout. Please try again.';
      const finalMsg = typeof detailMsg === 'string' ? detailMsg : JSON.stringify(detailMsg);
      setError(finalMsg);
      toast.error(isDuplicate ? 'You already have an active plan' : 'Checkout failed');
    } finally {
      setLoadingPlan(null);
    }
  };

  const confirmPlanChange = async () => {
    if (!previewData) return;
    setError(null);
    setLoadingPlan(previewData.targetPlan);
    try {
      const res = await api.post('/payments/change-plan', {
        newPlanType: previewData.targetPlan,
        originUrl: window.location.origin,
      });
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
        return;
      }
      toast.success('Plan change scheduled');
      setPreviewData(null);
      onOpenChange(false);
      if (onChanged) onChanged();
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Could not update your plan. Please try again.';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoadingPlan(null);
    }
  };

  const planOrder = ['payg', 'monthly', 'annual', 'multisite'];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPreviewData(null); onOpenChange(v); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="plan-selection-dialog"
      >
        {previewData ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900" data-testid="plan-preview-title">
                {previewData.kind === 'upgrade' && `Upgrade to ${previewData.newPlan}?`}
                {previewData.kind === 'downgrade' && `Change to ${previewData.newPlan}?`}
                {previewData.kind === 'switch_to_payg' && 'Switch to Pay As You Go?'}
                {previewData.kind === 'switch_from_payg' && `Start ${previewData.newPlan} plan?`}
              </DialogTitle>
              <DialogDescription className="text-slate-600" data-testid="plan-preview-description">
                {previewData.explanation}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Current plan</div>
                  <div className="text-lg font-bold text-slate-900 mt-1 capitalize" data-testid="preview-current-plan">{previewData.currentPlan}</div>
                </div>
                <div className="rounded-lg border border-[#4338CA]/30 bg-[#4338CA]/5 p-3">
                  <div className="text-xs uppercase font-semibold text-[#4338CA] tracking-wide">New plan</div>
                  <div className="text-lg font-bold text-slate-900 mt-1 capitalize" data-testid="preview-new-plan">
                    {previewData.newPlan}
                    <span className="text-sm font-medium text-slate-500 ml-2">
                      {previewData.newAmountDisplay}
                      {previewData.newPlan === 'annual' ? '/year' : previewData.newPlan === 'payg' ? '/shift' : '/month'}
                    </span>
                  </div>
                </div>
              </div>
              {previewData.effective === 'period_end' && previewData.effectiveAt && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="preview-effective-at">
                  Effective from <strong>{new Date(previewData.effectiveAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>. You can cancel this scheduled change before it takes effect.
                </div>
              )}
              {previewData.prorationAmountPence > 0 && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="preview-proration">
                  Immediate pro-rata charge: <strong>{previewData.prorationDisplay}</strong>
                </div>
              )}
              {previewData.prorationAmountPence < 0 && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="preview-proration-credit">
                  Immediate credit: <strong>{previewData.prorationDisplay}</strong> — applied to your next invoice.
                </div>
              )}
              {error && (
                <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="plan-selection-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => setPreviewData(null)}
                disabled={loadingPlan !== null}
                data-testid="preview-back-btn"
              >
                Back to plans
              </Button>
              <Button
                onClick={confirmPlanChange}
                disabled={loadingPlan !== null}
                className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white h-11 px-6"
                data-testid="preview-confirm-btn"
              >
                {loadingPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  previewData.kind === 'switch_from_payg' ? 'Continue to Stripe' : 'Confirm plan change'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">
            Choose your ShiftCover365 plan
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            You can change or cancel any time from this page. All plans include a 14-day
            free trial for new managers.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800"
            data-testid="plan-selection-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {planOrder.map((code) => {
            const plan = PLANS[code];
            if (!plan) return null;
            const isCurrent = currentPlanType === code;
            const isMonthly = code === 'monthly';
            const isAnnual = code === 'annual';
            const isPayg = code === 'payg';
            const isMultisite = code === 'multisite';
            const showFoundingOffer = isMonthly && foundingEligible;
            const displayed = showFoundingOffer
              ? displayPrice(FOUNDING_MEMBER_PRICE_PENCE)
              : displayPrice(plan.amountPence);
            const suffix = isPayg
              ? '/shift, billed monthly'
              : isAnnual
                ? '/year'
                : '/month';
            return (
              <div
                key={code}
                className={`relative rounded-2xl border p-5 flex flex-col ${
                  showFoundingOffer
                    ? 'border-amber-300 bg-amber-50/40'
                    : 'border-slate-200 bg-white'
                }`}
                data-testid={`${plan.testid}-card`}
              >
                {showFoundingOffer && (
                  <Badge
                    className="absolute -top-2 right-4 bg-amber-500 hover:bg-amber-500 text-white gap-1"
                    data-testid="founding-member-badge"
                  >
                    <Sparkles className="h-3 w-3" />
                    Founding Member
                  </Badge>
                )}
                {isCurrent && (
                  <Badge
                    variant="outline"
                    className="absolute -top-2 left-4 bg-emerald-50 border-emerald-300 text-emerald-800"
                    data-testid={`${plan.testid}-current-badge`}
                  >
                    Current plan
                  </Badge>
                )}
                <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{plan.tagline}</p>
                <div className="flex items-baseline gap-1 mt-3">
                  <span
                    className="text-3xl font-extrabold text-slate-900 tabular-nums"
                    data-testid={`${plan.testid}-price`}
                  >
                    {displayed}
                  </span>
                  <span className="text-sm font-medium text-slate-500">{suffix}</span>
                </div>
                {showFoundingOffer && (
                  <p className="text-xs text-amber-800 mt-1" data-testid="founding-member-line">
                    First 20 customers — {foundingStatus?.seatsRemaining} seats left.
                    Locked in for as long as the subscription stays active.
                  </p>
                )}
                {isAnnual && (
                  <p className="text-xs text-emerald-700 font-semibold mt-1">
                    Equivalent to {annualMonthlyEquivalent()}/month
                  </p>
                )}
                {isMultisite && (
                  <p className="text-xs text-slate-600 mt-1">
                    Up to {plan.maxLocations} locations included.
                  </p>
                )}
                {isPayg && (
                  <p className="text-xs text-slate-600 mt-1">
                    No monthly minimum — Stripe consolidates every completed shift
                    into a single monthly invoice.
                  </p>
                )}
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700 flex-1">
                  {(isPayg
                    ? [
                        'Pay only for shifts you actually cover',
                        'One consolidated monthly invoice',
                        'Automatic fallback after your free trial',
                      ]
                    : isMonthly
                      ? [
                          'Unlimited shifts at your location',
                          '14-day free trial',
                          'Cancel any time',
                        ]
                      : isAnnual
                        ? [
                            'Unlimited shifts at your location',
                            'Two months free vs paying monthly',
                            '14-day free trial',
                          ]
                        : [
                            'Unlimited shifts, up to 5 locations',
                            '14-day free trial',
                            'One invoice for all sites',
                          ]
                  ).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-4 h-11 rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                  onClick={() => handleChoose(code)}
                  disabled={loadingPlan !== null || isCurrent}
                  data-testid={`${plan.testid}-select-btn`}
                >
                  {loadingPlan === code ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCurrent ? (
                    'Current plan'
                  ) : hasActiveSub ? (
                    // Show upgrade/downgrade/switch label — the confirm
                    // dialog on submit tells the user exactly what happens.
                    code === 'payg'
                      ? 'Switch to PAYG'
                      : (['payg'].includes(currentPlanType) || (currentPlanType === 'monthly' && ['annual', 'multisite'].includes(code)))
                        ? `Upgrade to ${plan.name}`
                        : `Change to ${plan.name}`
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t pt-4 mt-4 flex-col sm:flex-row gap-2 sm:justify-between">
          <p className="text-xs text-slate-500">
            You&apos;ll be redirected to Stripe&apos;s secure checkout to enter card details.
          </p>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loadingPlan !== null}
            data-testid="plan-selection-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
